import { getEmailContent, getEmailSubject, getEmailFrom, getEmailDate, getEmailContext, } from '../../../lib/outlook';
import { formatEmailBody } from '../../../lib/formatEmailBody';
import { isComposeMode } from '../../../types/office';
// --- Helpers ---
const MAX_ATTACHMENT_BASE64_LENGTH = 10_485_760; // ~7.5 MB decoded
function ok(text) {
    return { content: [{ type: 'text', text }] };
}
function json(data) {
    return { content: [{ type: 'text', text: JSON.stringify(data, null, 2) }] };
}
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function validateEmail(email) {
    if (!EMAIL_REGEX.test(email)) {
        throw new Error(`Invalid email address: "${email}"`);
    }
}
function unsupported(feature) {
    return ok(`${feature} is not supported in this version of Outlook`);
}
function requireMailbox() {
    const mailbox = Office.context.mailbox;
    if (!mailbox)
        throw new Error('Mailbox not available');
    return mailbox;
}
function requireItem() {
    const item = requireMailbox().item;
    if (!item)
        throw new Error('No mailbox item available');
    return item;
}
function requireComposeItem() {
    const item = requireItem();
    if (!isComposeMode(item))
        throw new Error('This tool is only available in compose mode');
    return item;
}
function requireAppointmentItem() {
    const item = requireItem();
    if (item.itemType !== Office.MailboxEnums.ItemType.Appointment) {
        throw new Error('Current item is not an appointment');
    }
    return item;
}
function requireComposeAppointmentItem() {
    const item = requireComposeItem();
    if (item.itemType !== Office.MailboxEnums.ItemType.Appointment) {
        throw new Error('Current item is not an appointment');
    }
    return item;
}
function wrapAsync(fn, errorMsg) {
    return new Promise((resolve, reject) => {
        fn((result) => {
            if (result.status === Office.AsyncResultStatus.Failed) {
                reject(new Error(result.error?.message ?? errorMsg));
            }
            else {
                resolve(result.value);
            }
        });
    });
}
function getRecipientsAsync(field) {
    if (!field || typeof field.getAsync !== 'function')
        return Promise.resolve([]);
    return new Promise((resolve) => {
        field.getAsync((result) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
                resolve(result.value ?? []);
            }
            else {
                resolve([]);
            }
        });
    });
}
// --- Tool Definitions ---
export const outlookTools = [
    // =====================
    // READ TOOLS
    // =====================
    {
        tool: {
            name: 'outlook_get_email_content',
            description: 'Get the body text of the current email',
            inputSchema: {
                type: 'object',
                properties: {},
            },
        },
        execute: async () => {
            const content = await getEmailContent();
            return ok(content);
        },
    },
    {
        tool: {
            name: 'outlook_get_email_metadata',
            description: 'Get metadata (subject, from, date) of the current email',
            inputSchema: {
                type: 'object',
                properties: {},
            },
        },
        execute: async () => {
            const subject = getEmailSubject();
            const from = getEmailFrom();
            const date = getEmailDate();
            return json({ subject, from, date });
        },
    },
    {
        tool: {
            name: 'outlook_get_email_context',
            description: 'Get the full email context including subject, sender, date, and body content',
            inputSchema: {
                type: 'object',
                properties: {},
            },
        },
        execute: async () => {
            const context = await getEmailContext();
            return ok(context);
        },
    },
    {
        tool: {
            name: 'outlook_get_recipients',
            description: 'Get recipients (to, cc, bcc) of the current email',
            inputSchema: {
                type: 'object',
                properties: {},
            },
        },
        execute: async () => {
            const item = requireItem();
            if (isComposeMode(item)) {
                const raw = item;
                const [to, cc, bcc] = await Promise.all([
                    getRecipientsAsync(raw.to),
                    getRecipientsAsync(raw.cc),
                    getRecipientsAsync(raw.bcc),
                ]);
                return json({ to, cc, bcc });
            }
            else {
                const to = item.to ?? [];
                const cc = item.cc ?? [];
                return json({ to, cc });
            }
        },
    },
    {
        tool: {
            name: 'outlook_get_item_type',
            description: 'Get the type of the current item (Message or Appointment) and its mode (read or compose)',
            inputSchema: {
                type: 'object',
                properties: {},
            },
        },
        execute: async () => {
            const item = requireItem();
            const itemType = item.itemType ?? 'unknown';
            const mode = isComposeMode(item) ? 'compose' : 'read';
            return json({ itemType, mode });
        },
    },
    {
        tool: {
            name: 'outlook_get_conversation_id',
            description: 'Get the conversation ID and internet message ID of the current email for thread tracking',
            inputSchema: {
                type: 'object',
                properties: {},
            },
        },
        execute: async () => {
            const item = requireItem();
            return json({
                conversationId: item.conversationId ?? null,
                internetMessageId: item.internetMessageId ?? null,
                normalizedSubject: item.normalizedSubject ?? null,
            });
        },
    },
    {
        tool: {
            name: 'outlook_get_body_type',
            description: 'Get the content type of the email body (HTML or Text)',
            inputSchema: {
                type: 'object',
                properties: {},
            },
        },
        execute: async () => {
            const item = requireItem();
            if (!item.body || typeof item.body.getTypeAsync !== 'function') {
                return unsupported('body.getTypeAsync');
            }
            const bodyType = await wrapAsync((cb) => item.body.getTypeAsync(cb), 'Failed to get body type');
            return json({ bodyType });
        },
    },
    {
        tool: {
            name: 'outlook_get_attachments',
            description: 'Get metadata for all attachments on the current email (name, size, type, isInline)',
            inputSchema: {
                type: 'object',
                properties: {},
            },
        },
        execute: async () => {
            const item = requireItem();
            const attachments = item.attachments ?? [];
            const result = attachments.map((a) => ({
                id: a.id,
                name: a.name,
                size: a.size,
                contentType: a.contentType,
                isInline: a.isInline,
                attachmentType: a.attachmentType,
            }));
            return json(result);
        },
    },
    {
        tool: {
            name: 'outlook_get_categories',
            description: 'Get the categories assigned to the current email item',
            inputSchema: {
                type: 'object',
                properties: {},
            },
        },
        execute: async () => {
            const item = requireItem();
            if (!item.categories || typeof item.categories.getAsync !== 'function') {
                return unsupported('Categories API');
            }
            const categories = await wrapAsync((cb) => item.categories.getAsync(cb), 'Failed to get categories');
            return json(categories);
        },
    },
    {
        tool: {
            name: 'outlook_get_custom_properties',
            description: 'Get all custom properties stored on the current email item by this add-in',
            inputSchema: {
                type: 'object',
                properties: {},
            },
        },
        execute: async () => {
            const item = requireItem();
            const customProps = await wrapAsync((cb) => item.loadCustomPropertiesAsync(cb), 'Failed to load custom properties');
            const all = {};
            const keys = typeof customProps.getAll === 'function' ? Object.keys(customProps.getAll()) : [];
            for (const key of keys) {
                all[key] = customProps.get(key);
            }
            return json(all);
        },
    },
    {
        tool: {
            name: 'outlook_get_user_profile',
            description: "Get the current user's profile information (display name, email address, timezone, account type)",
            inputSchema: {
                type: 'object',
                properties: {},
            },
        },
        execute: async () => {
            const mailbox = requireMailbox();
            const profile = mailbox.userProfile;
            return json({
                displayName: profile?.displayName ?? null,
                emailAddress: profile?.emailAddress ?? null,
                timeZone: profile?.timeZone ?? null,
                accountType: profile?.accountType ?? null,
            });
        },
    },
    {
        tool: {
            name: 'outlook_get_appointment_details',
            description: 'Get details of the current appointment/meeting (start, end, location, attendees, recurrence)',
            inputSchema: {
                type: 'object',
                properties: {},
            },
        },
        execute: async () => {
            const item = requireAppointmentItem();
            if (isComposeMode(item)) {
                const raw = item;
                const getTimeAsync = (timeObj) => {
                    if (!timeObj || typeof timeObj.getAsync !== 'function')
                        return Promise.resolve(null);
                    return wrapAsync((cb) => timeObj.getAsync(cb), 'Failed to get time');
                };
                const getLocationAsync = (locObj) => {
                    if (!locObj || typeof locObj.getAsync !== 'function')
                        return Promise.resolve(null);
                    return wrapAsync((cb) => locObj.getAsync(cb), 'Failed to get location');
                };
                const [start, end, location, required, optional] = await Promise.all([
                    getTimeAsync(raw.start),
                    getTimeAsync(raw.end),
                    getLocationAsync(raw.location),
                    getRecipientsAsync(raw.requiredAttendees),
                    getRecipientsAsync(raw.optionalAttendees),
                ]);
                return json({
                    start: start?.toISOString() ?? null,
                    end: end?.toISOString() ?? null,
                    location,
                    requiredAttendees: required,
                    optionalAttendees: optional,
                });
            }
            else {
                return json({
                    start: item.start?.toISOString() ?? null,
                    end: item.end?.toISOString() ?? null,
                    location: item.location ?? null,
                    requiredAttendees: item.requiredAttendees ?? [],
                    optionalAttendees: item.optionalAttendees ?? [],
                    organizer: item.organizer
                        ? {
                            displayName: item.organizer.displayName,
                            emailAddress: item.organizer.emailAddress,
                        }
                        : null,
                    recurrence: item.recurrence ?? null,
                    isAllDayEvent: item.isAllDayEvent ?? null,
                });
            }
        },
    },
    // =====================
    // COMPOSE TOOLS
    // =====================
    {
        tool: {
            name: 'outlook_set_email_body',
            description: 'Set the body of the current email (compose mode only). Replaces the entire body content.',
            inputSchema: {
                type: 'object',
                properties: {
                    body: {
                        type: 'string',
                        description: 'The body content to set',
                    },
                    content_type: {
                        type: 'string',
                        enum: ['html', 'text'],
                        description: 'Content type: "html" or "text" (default: "html")',
                    },
                },
                required: ['body'],
            },
        },
        execute: async (args) => {
            const body = args.body;
            const contentType = args.content_type ?? 'html';
            const item = requireComposeItem();
            if (!item.body)
                throw new Error('Body not available on this item');
            const coercionType = contentType === 'text' ? Office.CoercionType.Text : Office.CoercionType.Html;
            const bodyToSet = contentType === 'html' ? formatEmailBody(body) : body;
            await wrapAsync((cb) => item.body.setAsync(bodyToSet, { coercionType }, cb), 'Failed to set email body');
            return ok('Email body updated successfully');
        },
    },
    {
        tool: {
            name: 'outlook_prepend_email_body',
            description: 'Prepend content to the beginning of the email body without replacing existing content (compose mode only)',
            inputSchema: {
                type: 'object',
                properties: {
                    body: {
                        type: 'string',
                        description: 'The content to prepend to the body',
                    },
                    content_type: {
                        type: 'string',
                        enum: ['html', 'text'],
                        description: 'Content type: "html" or "text" (default: "html")',
                    },
                },
                required: ['body'],
            },
        },
        execute: async (args) => {
            const body = args.body;
            const contentType = args.content_type ?? 'html';
            const item = requireComposeItem();
            if (!item.body || typeof item.body.prependAsync !== 'function') {
                return unsupported('body.prependAsync');
            }
            const coercionType = contentType === 'text' ? Office.CoercionType.Text : Office.CoercionType.Html;
            const bodyToPrepend = contentType === 'html' ? formatEmailBody(body) : body;
            await wrapAsync((cb) => item.body.prependAsync(bodyToPrepend, { coercionType }, cb), 'Failed to prepend email body');
            return ok('Content prepended to email body successfully');
        },
    },
    {
        tool: {
            name: 'outlook_set_email_subject',
            description: 'Set the subject of the current email (compose mode only)',
            inputSchema: {
                type: 'object',
                properties: {
                    subject: {
                        type: 'string',
                        description: 'The subject to set',
                    },
                },
                required: ['subject'],
            },
        },
        execute: async (args) => {
            const subject = args.subject;
            const item = requireComposeItem();
            if (!item.subject)
                throw new Error('Subject not available on this item');
            await wrapAsync((cb) => item.subject.setAsync(subject, cb), 'Failed to set subject');
            return ok(`Email subject set to "${subject}"`);
        },
    },
    {
        tool: {
            name: 'outlook_add_recipient',
            description: 'Add a recipient to the current email (compose mode only)',
            inputSchema: {
                type: 'object',
                properties: {
                    email: {
                        type: 'string',
                        description: 'The email address of the recipient',
                    },
                    name: {
                        type: 'string',
                        description: 'Display name for the recipient (optional)',
                    },
                    field: {
                        type: 'string',
                        enum: ['to', 'cc', 'bcc'],
                        description: 'Which recipient field to add to: "to", "cc", or "bcc"',
                    },
                },
                required: ['email', 'field'],
            },
        },
        execute: async (args) => {
            const email = args.email;
            validateEmail(email);
            const name = args.name ?? email;
            const field = args.field;
            const item = requireComposeItem();
            const recipientField = item[field];
            if (!recipientField || typeof recipientField.addAsync !== 'function') {
                throw new Error(`Recipient field "${field}" not available`);
            }
            const recipient = [{ emailAddress: email, displayName: name }];
            await wrapAsync((cb) => recipientField.addAsync(recipient, cb), 'Failed to add recipient');
            return ok(`Added ${name} (${email}) to ${field}`);
        },
    },
    {
        tool: {
            name: 'outlook_remove_recipient',
            description: 'Remove a recipient from the current email by email address (compose mode only). Replaces the recipient list with all current recipients minus the specified one.',
            inputSchema: {
                type: 'object',
                properties: {
                    email: {
                        type: 'string',
                        description: 'The email address of the recipient to remove',
                    },
                    field: {
                        type: 'string',
                        enum: ['to', 'cc', 'bcc'],
                        description: 'Which recipient field to remove from: "to", "cc", or "bcc"',
                    },
                },
                required: ['email', 'field'],
            },
        },
        execute: async (args) => {
            validateEmail(args.email);
            const email = args.email.toLowerCase();
            const field = args.field;
            const item = requireComposeItem();
            const recipientField = item[field];
            if (!recipientField ||
                typeof recipientField.getAsync !== 'function' ||
                typeof recipientField.setAsync !== 'function') {
                throw new Error(`Recipient field "${field}" not available`);
            }
            const current = await getRecipientsAsync(recipientField);
            const filtered = current.filter((r) => r.emailAddress.toLowerCase() !== email);
            if (filtered.length === current.length) {
                throw new Error(`No recipient with email "${args.email}" found in ${field}`);
            }
            await wrapAsync((cb) => recipientField.setAsync(filtered, cb), 'Failed to update recipients');
            return ok(`Removed ${args.email} from ${field}`);
        },
    },
    {
        tool: {
            name: 'outlook_add_attachment',
            description: 'Add a file attachment to the current email from Base64-encoded data (compose mode only)',
            inputSchema: {
                type: 'object',
                properties: {
                    base64: {
                        type: 'string',
                        description: 'Base64-encoded file content',
                    },
                    name: {
                        type: 'string',
                        description: 'File name for the attachment (e.g. "report.pdf")',
                    },
                    is_inline: {
                        type: 'boolean',
                        description: 'Whether the attachment is inline (used for embedded images). Default: false',
                    },
                },
                required: ['base64', 'name'],
            },
        },
        execute: async (args) => {
            const base64 = args.base64;
            const name = args.name;
            const isInline = args.is_inline ?? false;
            if (base64.length > MAX_ATTACHMENT_BASE64_LENGTH) {
                const sizeMB = ((base64.length * 3) / 4 / 1_048_576).toFixed(1);
                throw new Error(`Attachment too large (${sizeMB} MB). Maximum allowed is ~7.5 MB.`);
            }
            const item = requireComposeItem();
            if (typeof item.addFileAttachmentFromBase64Async !== 'function') {
                return unsupported('addFileAttachmentFromBase64Async (requires Mailbox 1.8+)');
            }
            const attachmentId = await wrapAsync((cb) => item.addFileAttachmentFromBase64Async(base64, name, { isInline }, cb), 'Failed to add attachment');
            return ok(`Attachment "${name}" added successfully (id: ${attachmentId})`);
        },
    },
    {
        tool: {
            name: 'outlook_remove_attachment',
            description: 'Remove an attachment from the current email by attachment ID (compose mode only). Use outlook_get_attachments to get IDs.',
            inputSchema: {
                type: 'object',
                properties: {
                    attachment_id: {
                        type: 'string',
                        description: 'The attachment ID to remove',
                    },
                },
                required: ['attachment_id'],
            },
        },
        execute: async (args) => {
            const attachmentId = args.attachment_id;
            const item = requireComposeItem();
            if (typeof item.removeAttachmentAsync !== 'function') {
                return unsupported('removeAttachmentAsync');
            }
            await wrapAsync((cb) => item.removeAttachmentAsync(attachmentId, cb), 'Failed to remove attachment');
            return ok(`Attachment "${attachmentId}" removed successfully`);
        },
    },
    {
        tool: {
            name: 'outlook_set_categories',
            description: 'Add or remove categories on the current email item (requires Mailbox 1.8+)',
            inputSchema: {
                type: 'object',
                properties: {
                    action: {
                        type: 'string',
                        enum: ['add', 'remove'],
                        description: 'Whether to add or remove the specified categories',
                    },
                    categories: {
                        type: 'array',
                        items: { type: 'string' },
                        description: 'Array of category names to add or remove',
                    },
                },
                required: ['action', 'categories'],
            },
        },
        execute: async (args) => {
            const action = args.action;
            const categories = args.categories;
            const item = requireItem();
            if (!item.categories) {
                return unsupported('Categories API');
            }
            if (action === 'add') {
                if (typeof item.categories.addAsync !== 'function') {
                    return unsupported('categories.addAsync');
                }
                await wrapAsync((cb) => item.categories.addAsync(categories, cb), 'Failed to add categories');
            }
            else {
                if (typeof item.categories.removeAsync !== 'function') {
                    return unsupported('categories.removeAsync');
                }
                await wrapAsync((cb) => item.categories.removeAsync(categories, cb), 'Failed to remove categories');
            }
            return ok(`Categories ${action === 'add' ? 'added' : 'removed'}: ${categories.join(', ')}`);
        },
    },
    {
        tool: {
            name: 'outlook_set_custom_properties',
            description: 'Set custom properties on the current email item. Properties persist with the item and are only accessible to this add-in.',
            inputSchema: {
                type: 'object',
                properties: {
                    properties: {
                        type: 'object',
                        description: 'Key-value pairs to set. Values can be strings, numbers, or booleans.',
                    },
                },
                required: ['properties'],
            },
        },
        execute: async (args) => {
            const properties = args.properties;
            const item = requireItem();
            const customProps = await wrapAsync((cb) => item.loadCustomPropertiesAsync(cb), 'Failed to load custom properties');
            for (const [key, value] of Object.entries(properties)) {
                customProps.set(key, value);
            }
            await wrapAsync((cb) => customProps.saveAsync(cb), 'Failed to save custom properties');
            return ok(`Custom properties set: ${Object.keys(properties).join(', ')}`);
        },
    },
    {
        tool: {
            name: 'outlook_add_notification',
            description: 'Show an informational or error notification bar on the current email item',
            inputSchema: {
                type: 'object',
                properties: {
                    key: {
                        type: 'string',
                        description: 'Unique key to identify this notification (used for replacing/removing)',
                    },
                    message: {
                        type: 'string',
                        description: 'The notification message to display',
                    },
                    type: {
                        type: 'string',
                        enum: ['informational', 'error'],
                        description: 'Notification type (default: "informational")',
                    },
                    persistent: {
                        type: 'boolean',
                        description: 'Whether the notification persists until dismissed. Only for informational type. Default: false',
                    },
                },
                required: ['key', 'message'],
            },
        },
        execute: async (args) => {
            const key = args.key;
            const message = args.message;
            const type = args.type ?? 'informational';
            const persistent = args.persistent ?? false;
            const item = requireItem();
            if (!item.notificationMessages ||
                typeof item.notificationMessages.addAsync !== 'function') {
                return unsupported('Notification API');
            }
            const notificationType = type === 'error'
                ? Office.MailboxEnums.ItemNotificationMessageType.ErrorMessage
                : Office.MailboxEnums.ItemNotificationMessageType.InformationalMessage;
            await wrapAsync((cb) => item.notificationMessages.addAsync(key, {
                type: notificationType,
                message,
                persistent,
            }, cb), 'Failed to add notification');
            return ok(`Notification "${key}" added`);
        },
    },
    {
        tool: {
            name: 'outlook_compose_new_message',
            description: 'Open a new message compose form with optional pre-filled fields (to, cc, bcc, subject, body)',
            inputSchema: {
                type: 'object',
                properties: {
                    to: {
                        type: 'array',
                        items: { type: 'string' },
                        description: 'Array of recipient email addresses for the To field',
                    },
                    cc: {
                        type: 'array',
                        items: { type: 'string' },
                        description: 'Array of recipient email addresses for the CC field',
                    },
                    bcc: {
                        type: 'array',
                        items: { type: 'string' },
                        description: 'Array of recipient email addresses for the BCC field',
                    },
                    subject: {
                        type: 'string',
                        description: 'Subject line for the new message',
                    },
                    body: {
                        type: 'string',
                        description: 'HTML body content for the new message',
                    },
                },
            },
        },
        execute: async (args) => {
            const mailbox = requireMailbox();
            const toRecipients = args.to ?? [];
            const ccRecipients = args.cc ?? [];
            const bccRecipients = args.bcc ?? [];
            for (const email of [...toRecipients, ...ccRecipients, ...bccRecipients]) {
                validateEmail(email);
            }
            const subject = args.subject ?? '';
            const body = args.body ?? '';
            const params = {};
            if (toRecipients.length)
                params.toRecipients = toRecipients.map((e) => ({ emailAddress: e }));
            if (ccRecipients.length)
                params.ccRecipients = ccRecipients.map((e) => ({ emailAddress: e }));
            if (bccRecipients.length)
                params.bccRecipients = bccRecipients.map((e) => ({ emailAddress: e }));
            if (subject)
                params.subject = subject;
            if (body)
                params.htmlBody = formatEmailBody(body);
            if (typeof mailbox.displayNewMessageFormAsync === 'function') {
                await wrapAsync((cb) => mailbox.displayNewMessageFormAsync(params, cb), 'Failed to open new message form');
            }
            else if (typeof mailbox.displayNewMessageForm === 'function') {
                mailbox.displayNewMessageForm(params);
            }
            else {
                return unsupported('displayNewMessageForm');
            }
            return ok('New message compose form opened');
        },
    },
    {
        tool: {
            name: 'outlook_compose_new_appointment',
            description: 'Open a new appointment form with optional pre-filled fields (attendees, start, end, location, subject, body)',
            inputSchema: {
                type: 'object',
                properties: {
                    required_attendees: {
                        type: 'array',
                        items: { type: 'string' },
                        description: 'Array of required attendee email addresses',
                    },
                    optional_attendees: {
                        type: 'array',
                        items: { type: 'string' },
                        description: 'Array of optional attendee email addresses',
                    },
                    start: {
                        type: 'string',
                        description: 'Start time as ISO 8601 string (e.g. "2025-03-15T09:00:00")',
                    },
                    end: {
                        type: 'string',
                        description: 'End time as ISO 8601 string (e.g. "2025-03-15T10:00:00")',
                    },
                    location: {
                        type: 'string',
                        description: 'Location of the appointment',
                    },
                    subject: {
                        type: 'string',
                        description: 'Subject of the appointment',
                    },
                    body: {
                        type: 'string',
                        description: 'HTML body content for the appointment',
                    },
                },
            },
        },
        execute: async (args) => {
            const mailbox = requireMailbox();
            const params = {};
            const required = args.required_attendees ?? [];
            const optional = args.optional_attendees ?? [];
            for (const email of [...required, ...optional]) {
                validateEmail(email);
            }
            if (required.length)
                params.requiredAttendees = required.map((e) => ({ emailAddress: e }));
            if (optional.length)
                params.optionalAttendees = optional.map((e) => ({ emailAddress: e }));
            if (args.start)
                params.start = new Date(args.start);
            if (args.end)
                params.end = new Date(args.end);
            if (args.location)
                params.location = args.location;
            if (args.subject)
                params.subject = args.subject;
            if (args.body)
                params.body = formatEmailBody(args.body);
            if (typeof mailbox.displayNewAppointmentFormAsync === 'function') {
                await wrapAsync((cb) => mailbox.displayNewAppointmentFormAsync(params, cb), 'Failed to open new appointment form');
            }
            else if (typeof mailbox.displayNewAppointmentForm === 'function') {
                mailbox.displayNewAppointmentForm(params);
            }
            else {
                return unsupported('displayNewAppointmentForm');
            }
            return ok('New appointment form opened');
        },
    },
    {
        tool: {
            name: 'outlook_set_appointment_time',
            description: 'Set the start and/or end time of the current appointment (compose mode only)',
            inputSchema: {
                type: 'object',
                properties: {
                    start: {
                        type: 'string',
                        description: 'Start time as ISO 8601 string (e.g. "2025-03-15T09:00:00")',
                    },
                    end: {
                        type: 'string',
                        description: 'End time as ISO 8601 string (e.g. "2025-03-15T10:00:00")',
                    },
                },
            },
        },
        execute: async (args) => {
            const item = requireComposeAppointmentItem();
            const results = [];
            if (args.start) {
                if (!item.start || typeof item.start.setAsync !== 'function') {
                    return unsupported('start.setAsync');
                }
                await wrapAsync((cb) => item.start.setAsync(new Date(args.start), cb), 'Failed to set start time');
                results.push(`Start time set to ${args.start}`);
            }
            if (args.end) {
                if (!item.end || typeof item.end.setAsync !== 'function') {
                    return unsupported('end.setAsync');
                }
                await wrapAsync((cb) => item.end.setAsync(new Date(args.end), cb), 'Failed to set end time');
                results.push(`End time set to ${args.end}`);
            }
            if (results.length === 0) {
                return ok('No start or end time provided');
            }
            return ok(results.join('. '));
        },
    },
    {
        tool: {
            name: 'outlook_set_appointment_location',
            description: 'Set the location of the current appointment (compose mode only)',
            inputSchema: {
                type: 'object',
                properties: {
                    location: {
                        type: 'string',
                        description: 'The location to set',
                    },
                },
                required: ['location'],
            },
        },
        execute: async (args) => {
            const location = args.location;
            const item = requireComposeAppointmentItem();
            if (!item.location || typeof item.location.setAsync !== 'function') {
                return unsupported('location.setAsync');
            }
            await wrapAsync((cb) => item.location.setAsync(location, cb), 'Failed to set location');
            return ok(`Appointment location set to "${location}"`);
        },
    },
    {
        tool: {
            name: 'outlook_add_attendee',
            description: 'Add a required or optional attendee to the current appointment (compose mode only)',
            inputSchema: {
                type: 'object',
                properties: {
                    email: {
                        type: 'string',
                        description: 'Email address of the attendee',
                    },
                    name: {
                        type: 'string',
                        description: 'Display name of the attendee (optional)',
                    },
                    type: {
                        type: 'string',
                        enum: ['required', 'optional'],
                        description: 'Whether the attendee is required or optional (default: "required")',
                    },
                },
                required: ['email'],
            },
        },
        execute: async (args) => {
            const email = args.email;
            validateEmail(email);
            const name = args.name ?? email;
            const type = args.type ?? 'required';
            const item = requireComposeAppointmentItem();
            const attendeeField = type === 'optional' ? item.optionalAttendees : item.requiredAttendees;
            if (!attendeeField || typeof attendeeField.addAsync !== 'function') {
                return unsupported(`${type}Attendees.addAsync`);
            }
            const attendee = [{ emailAddress: email, displayName: name }];
            await wrapAsync((cb) => attendeeField.addAsync(attendee, cb), 'Failed to add attendee');
            return ok(`Added ${name} (${email}) as ${type} attendee`);
        },
    },
    {
        tool: {
            name: 'outlook_display_message',
            description: 'Open an existing message in a new window by its item ID',
            inputSchema: {
                type: 'object',
                properties: {
                    item_id: {
                        type: 'string',
                        description: 'The Exchange item ID of the message to display',
                    },
                },
                required: ['item_id'],
            },
        },
        execute: async (args) => {
            const itemId = args.item_id;
            const mailbox = requireMailbox();
            if (typeof mailbox.displayMessageFormAsync === 'function') {
                await wrapAsync((cb) => mailbox.displayMessageFormAsync(itemId, cb), 'Failed to display message');
            }
            else if (typeof mailbox.displayMessageForm === 'function') {
                mailbox.displayMessageForm(itemId);
            }
            else {
                return unsupported('displayMessageForm');
            }
            return ok('Message form opened');
        },
    },
    {
        tool: {
            name: 'outlook_display_appointment',
            description: 'Open an existing appointment in a new window by its item ID',
            inputSchema: {
                type: 'object',
                properties: {
                    item_id: {
                        type: 'string',
                        description: 'The Exchange item ID of the appointment to display',
                    },
                },
                required: ['item_id'],
            },
        },
        execute: async (args) => {
            const itemId = args.item_id;
            const mailbox = requireMailbox();
            if (typeof mailbox.displayAppointmentFormAsync === 'function') {
                await wrapAsync((cb) => mailbox.displayAppointmentFormAsync(itemId, cb), 'Failed to display appointment');
            }
            else if (typeof mailbox.displayAppointmentForm === 'function') {
                mailbox.displayAppointmentForm(itemId);
            }
            else {
                return unsupported('displayAppointmentForm');
            }
            return ok('Appointment form opened');
        },
    },
];
